/**
 * Integration Settings Component
 *
 * TASK-226: Telegram account linking moved out of Settings → Integrations and
 * into the self-service "Профиль → Мои каналы" widget (the `telegram_system`
 * channel of ChannelsPanel).
 *
 * TASK-233 Phase 4: this tab hosts org-scoped OUTGOING channels rendered via the
 * reusable {@link ChannelSection} (the same UI used for user channels) with an
 * org-scoped save callback. v1 channel: `outgoing_webhook` (url / secret /
 * events) belongs to the whole organization.
 *
 * TASK-254: `outgoing_email` is now available on BOTH scopes — every operator
 * keeps a personal email (user channel, ChannelsPanel) AND the organization gets
 * a single company-wide email card here. Both org channels are rendered
 * generically from {@link ORG_CHANNEL_CODENAMES} through the same widget, so the
 * legacy hardcoded "Каналы связи" / Telegram-moved note has been removed.
 *
 * The props signature (settings/onChange/onSave/disabled) is kept intact so
 * SettingsPage compiles unchanged; they are no longer used here.
 *
 * KAN-718: above the "Email-уведомления" card sits the mailbox section
 * ({@link MailboxSection}): the box for correspondence with clients, distinct
 * from the notification address below.
 *
 * KAN-830: the manager bot is one platform-wide bot, organizations no longer
 * connect it here.
 */
import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Webhook, Mail, Loader2, ShieldCheck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChannelSection } from '@/components/users/ChannelSection';
import { orgChannelsApi } from '@/services/api/orgChannels';
import { useEventsMap } from '@/hooks/useEventsMap';
import { MailboxSection } from '@/components/settings/mail/MailboxSection';
import type { IntegrationsSettings as IntegrationsSettingsType } from '@/types/settings';

const WEBHOOK_CODENAME = 'outgoing_webhook';
const EMAIL_CODENAME = 'outgoing_email';

/**
 * Org-scoped outgoing channel rendered on this tab. Each descriptor drives one
 * {@link OrgChannelCard} (own query + save callback) so adding a channel is a
 * descriptor rather than a copy-pasted card.
 */
interface OrgChannelDescriptor {
  codename: string;
  title: string;
  icon: LucideIcon;
  /** Short explanation under the title (what this channel is for). */
  note?: string;
}

const WEBHOOK_CHANNEL: OrgChannelDescriptor = {
  codename: WEBHOOK_CODENAME,
  title: 'Исходящий webhook',
  icon: Webhook,
};

const EMAIL_CHANNEL: OrgChannelDescriptor = {
  codename: EMAIL_CODENAME,
  title: 'Email-уведомления',
  icon: Mail,
  note: 'Это адрес для уведомлений вам, не для переписки с клиентами.',
};

interface IntegrationSettingsProps {
  settings: IntegrationsSettingsType;
  onChange: (updates: Partial<IntegrationsSettingsType>) => void;
  onSave: () => void;
  disabled: boolean;
  /** KAN-718: scroll to the mailbox section (old ?tab=mail links). */
  focusMailbox?: boolean;
}

/**
 * One org-scoped outgoing channel card: fetches its config, forces the outgoing
 * route, and saves via the org channels API. Always-outgoing direction is set
 * explicitly because the backend response may omit it.
 */
function OrgChannelCard({
  descriptor,
  eventsMap,
}: {
  descriptor: OrgChannelDescriptor;
  eventsMap: Record<string, string>;
}) {
  const { codename, title, icon: Icon, note } = descriptor;

  const {
    data: channel,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['org-channel', codename],
    queryFn: () => orgChannelsApi.getOrgChannel(codename),
  });

  // Org channels are ALWAYS outgoing; the backend response may omit
  // `direction`, so set it explicitly here so ChannelSection renders the
  // outgoing route.
  const outgoingChannel = useMemo(
    () => (channel ? { ...channel, direction: 'outgoing' as const } : undefined),
    [channel]
  );

  const handleSave = useCallback(
    (body: { enabled: boolean; values: Record<string, unknown> }) =>
      orgChannelsApi.updateOrgChannel(codename, body),
    [codename]
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Icon className="w-5 h-5" />
          {title}
        </CardTitle>
        {note && (
          <p className="text-sm text-muted-foreground" data-testid={`org-channel-note-${codename}`}>
            {note}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {codename === WEBHOOK_CODENAME && (
          <div
            data-testid="webhook-hmac-info"
            className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Запросы подписываются заголовком X-Webhook-Signature (HMAC-SHA256),
              X-Event-Type.
            </span>
          </div>
        )}

        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Загрузка настроек канала...</span>
          </div>
        )}

        {isError && (
          <p className="text-sm text-destructive">
            Не удалось загрузить настройки канала.
          </p>
        )}

        {outgoingChannel && (
          <ChannelSection
            channel={outgoingChannel}
            onSave={handleSave}
            eventsMap={eventsMap}
          />
        )}
      </CardContent>
    </Card>
  );
}

export function IntegrationSettings({
  settings,
  onChange,
  onSave,
  disabled,
  focusMailbox = false,
}: IntegrationSettingsProps) {
  // Props kept for compatibility with SettingsPage; no longer used here.
  void settings;
  void onChange;
  void onSave;
  void disabled;

  // Event codename → human label for the outgoing route-header trigger events.
  const eventsMap = useEventsMap();

  return (
    <div data-testid="integrations-settings" className="space-y-6">
      <OrgChannelCard descriptor={WEBHOOK_CHANNEL} eventsMap={eventsMap} />
      <MailboxSection focus={focusMailbox} />
      <OrgChannelCard descriptor={EMAIL_CHANNEL} eventsMap={eventsMap} />
    </div>
  );
}
